import { NatsConnection } from 'nats.ws';

import router from '@/router/index';
import { useAuth } from '@/composables/useAuth';
import type { AttendancePayload } from '@/models/auth';

import { USER_ACTIVITY_EVENTS } from '@/utils/constants';
import { natsPublish, ActionHandlers } from '@/services/NatsService.ts';

interface ActiveTracker {
    stop: (isRefreshing?: boolean) => void;
    pulse: (newTokenExp: number | null, shouldUpdateActivity: boolean, bypassModalCheck?: boolean, force?: boolean) => void;
}

let activeTracker: ActiveTracker | null = null;
let persistentNC: any = null;
let lastTrackedUser: string | null = null;
let lastTrackedExp: number | null = null;
let lastUpdate = 0;

export const useNats = () => {
    const { user, triggerSessionWarning, updateAttendanceStatus, logout, executeTokenRefresh, modalType, showSessionModal } = useAuth();

    const activityActions: ActionHandlers = {
        sessionWarning: (data: any) => {
            triggerSessionWarning(data);
        },
        sessionExpired: async () => {
            const serviceId = router.currentRoute.value.params.serviceId as string;
            await logout(serviceId, '15');
        },
        updateLastActivity: () => {
            return { status: 'ok' };
        },
        attendanceStatus: (data: AttendancePayload) => {
            updateAttendanceStatus(data);
        },
        refreshToken: async (d: any) => {
            try {
                const updatedUser = await executeTokenRefresh();
                manageTracker(updatedUser.id, updatedUser.tokenExp, false, false, true);
            } catch (err) {
                console.error("NATS Action: Failed to handle automatic token refresh", err);
            }
        }
    };

    const startActivityTracker = (
        conn: NatsConnection,
        userId: string,
        initialTokenExp: number | null,
        initialUpdateActivity: boolean,
        initialBypassModalCheck: boolean
    ) => {
        const env = import.meta.env.VITE_ENV;
        const subject = `auth.${env}.activity`;
        const interval = 10000;

        let currentTokenExp = initialTokenExp;

        const performUpdate = (force = false, updateActivity = true, bypassModalCheck = false) => {
            if (!conn || conn.isClosed() || (!bypassModalCheck && showSessionModal.value && modalType.value === 'expired')) {
                return;
            }

            const now = Date.now();
            if (force || (now - lastUpdate > interval)) {
                lastUpdate = now;
                natsPublish(
                    conn,
                    subject,
                    activityActions,
                    'updateLastActivity',
                    {
                        userId,
                        tokenExp: currentTokenExp,
                        updateActivity: updateActivity
                    }
                );
            }
        };

        const eventHandler = () => performUpdate(false, true);

        USER_ACTIVITY_EVENTS.forEach(name => {
            window.addEventListener(name, eventHandler, { passive: true });
        });

        performUpdate(true, initialUpdateActivity, initialBypassModalCheck);

        return {
            pulse: (newTokenExp: number | null, shouldUpdateActivity: boolean, bypassModalCheck = false, force = false) => {
                if (newTokenExp !== null) {
                    currentTokenExp = newTokenExp;
                }
                performUpdate(force, shouldUpdateActivity, bypassModalCheck);
            },
            stop: (isRefreshing = false) => {
                USER_ACTIVITY_EVENTS.forEach(name => {
                    window.removeEventListener(name, eventHandler);
                });

                if (!isRefreshing && conn && !conn.isClosed()) {
                    natsPublish(conn, subject, activityActions, 'updateLastActivity', {
                        userId,
                        tokenExp: currentTokenExp,
                        stopTracking: true
                    });
                }
            }
        };
    };

    const manageTracker = (
        userId: string | null,
        tokenExp: number | null,
        updateActivity: boolean,
        bypassModalCheck = false,
        force = false
    ) => {
        return;
        /*
        ---------------temporary removed for demo---------------------
        const isConnectionActive = persistentNC && !persistentNC.isClosed();
        if (userId === null || !isConnectionActive) {
            if (activeTracker) {
                activeTracker.stop(false);
                activeTracker = null;
            }
            lastTrackedUser = null;
            lastTrackedExp = null;
            return;
        }

        if (userId && isConnectionActive) {
            const isSameUser = (userId === lastTrackedUser);

            if (isSameUser && activeTracker) {
                activeTracker.pulse(tokenExp, updateActivity, bypassModalCheck, force);
                lastTrackedExp = tokenExp;
                return;
            }

            if (activeTracker) {
                activeTracker.stop(isSameUser);
            }

            activeTracker = startActivityTracker(persistentNC, userId, tokenExp, updateActivity, bypassModalCheck);
            lastTrackedUser = userId;
            lastTrackedExp = tokenExp;
        }
         */
    };

    const setConnection = (conn: any) => {
        persistentNC = conn;
    };

    return {
        activityActions,
        setConnection,
        manageTracker
    };
};